import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, FadeIn } from "@/components/Reveal";
import { whatsappLink, WhatsAppIcon } from "@/components/FloatingWhatsApp";
import SEO from "@/components/SEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <main className="flex min-h-screen flex-col items-start justify-center px-6 md:px-12 lg:px-20" data-testid="product-not-found">
        <h1 className="font-display text-5xl font-bold uppercase leading-[1.08] tracking-normal">Product not found</h1>
        <Link to="/products" className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-brand">
          ← Back to products
        </Link>
      </main>
    );

  if (!product) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="product-loading" />;

  const seoTitle = product.standard
    ? `${product.name} (${product.standard}) — Catalyst Petrochem`
    : `${product.name} — Catalyst Petrochem`;
  const seoDescription = (
    (product.description || `${product.name} supplied by Catalyst Petrochem LLP.`) +
    (product.standard ? ` Conforms to ${product.standard}.` : "")
  ).slice(0, 154);

  return (
    <main data-testid="product-detail-page">
      <SEO
        title={seoTitle.slice(0, 60)}
        description={seoDescription}
        path={`/products/${product.slug}`}
        type="product"
      />
      <header className="border-b border-white/10 px-6 pb-16 pt-36 md:px-12 md:pt-44 lg:px-20">
        <FadeIn>
          <Link
            to="/products"
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-brand"
          >
            <ArrowLeft className="h-4 w-4" /> All products
          </Link>
        </FadeIn>
        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-brand" data-testid="product-category">
          {product.category}
        </p>
        <Reveal immediate delay={0.1} className="mt-4">
          <h1 className="font-display text-5xl font-bold uppercase leading-[1.08] tracking-normal sm:text-6xl sm:leading-[1.08] lg:text-7xl lg:leading-[1.08]" data-testid="product-name">
            {product.name}
          </h1>
        </Reveal>
        <FadeIn delay={0.25} className="mt-8 flex flex-wrap gap-3">
          {product.standard && (
            <span className="border border-brand/50 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-brand" data-testid="product-standard">
              {product.standard}
            </span>
          )}
          {product.packaging && (
            <span className="border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-400" data-testid="product-packaging">
              Packaging — {product.packaging}
            </span>
          )}
        </FadeIn>
      </header>

      <section className="grid gap-14 px-6 py-16 md:grid-cols-12 md:px-12 md:py-24 lg:px-20">
        <div className="md:col-span-5">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">Overview</p>
            <p className="mt-6 text-base leading-relaxed text-neutral-300 md:text-lg" data-testid="product-description">
              {product.description}
            </p>
            {product.applications?.length > 0 && (
              <div className="mt-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">Applications</p>
                <div className="mt-5 flex flex-wrap gap-2" data-testid="product-applications">
                  {product.applications.map((a) => (
                    <span key={a} className="border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-neutral-300">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to={`/contact?product=${encodeURIComponent(product.name)}`}
                data-testid="product-enquiry-btn"
                className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-deep"
              >
                Enquire about {product.name}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.slug}/spec-sheet`}
                download
                data-testid="product-spec-download"
                className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand hover:text-brand"
              >
                <FileDown className="h-4 w-4" /> Spec Sheet (PDF)
              </a>
              <a
                href={whatsappLink(product.name)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-whatsapp-btn"
                className="inline-flex items-center gap-3 border border-[#25D366]/50 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#25D366] transition-colors duration-300 hover:bg-[#25D366] hover:text-white"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
              </a>
            </div>
          </FadeIn>
        </div>

        <div className="md:col-span-7">
          {product.tables?.length > 0 ? (
            product.tables.map((t, ti) => (
              <FadeIn key={t.title} delay={ti * 0.08} className="mb-12 last:mb-0">
                <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brand" data-testid={`spec-table-title-${ti}`}>
                  {t.title}
                </p>
                <div className="overflow-x-auto border border-white/10" data-testid={`spec-table-${ti}`}>
                  <table className="w-full font-mono text-xs md:text-sm">
                    <thead>
                      <tr>
                        {t.headers.map((h) => (
                          <th
                            key={h}
                            className="border-b border-white/10 bg-white/[0.04] px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[0.15em] text-neutral-400"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {t.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-white/5 last:border-b-0 transition-colors hover:bg-white/[0.02]">
                          {row.map((cell, ci) => (
                            <td key={ci} className={`px-4 py-3 ${ci === 0 ? "text-neutral-400" : "text-white"}`}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </FadeIn>
            ))
          ) : (
            <FadeIn className="flex h-full items-center border border-white/10 bg-[#0C0C0C] p-10">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">Technical Data</p>
                <p className="mt-4 text-sm leading-relaxed text-neutral-400">
                  Detailed technical data sheets and joint test certificates are shared on request. Contact our team
                  for grade-specific documentation and current availability.
                </p>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </main>
  );
}
